import { useNavigate } from "react-router-dom";
import {
  dashboardStats,
  liveStatus,
  recentActivity,
  pendingTasks,
  type LiveStatus,
} from "@/mocks/admin";

const quickActions = [
  { label: "New News", icon: "ri-add-line", path: "/admin/news" },
  { label: "Upload Video", icon: "ri-upload-line", path: "/admin/videos" },
  { label: "Configure Live TV", icon: "ri-live-line", path: "/admin/live" },
  { label: "New Program", icon: "ri-tv-line", path: "/admin/programs" },
  { label: "Add Schedule", icon: "ri-calendar-check-line", path: "/admin/schedule" },
  { label: "Breaking News", icon: "ri-flashlight-line", path: "/admin/breaking" },
  { label: "Upload Gallery", icon: "ri-image-line", path: "/admin/gallery" },
  { label: "New Direct Session", icon: "ri-broadcast-line", path: "/admin/direct" },
];

const activityIcons: Record<string, string> = {
  news: "ri-newspaper-line",
  video: "ri-movie-line",
  live: "ri-live-line",
  program: "ri-tv-line",
  settings: "ri-settings-3-line",
  user: "ri-user-line",
};

const priorityStyles: Record<string, string> = {
  high: "bg-accent-100 text-accent-700",
  medium: "bg-secondary-100 text-secondary-700",
  low: "bg-background-100 text-foreground-600",
};

function liveColor(status: LiveStatus["status"]) {
  switch (status) {
    case "live":
      return { dot: "bg-green-500", text: "text-green-700", label: "L+TV LIVE" };
    case "warning":
      return { dot: "bg-yellow-500", text: "text-yellow-700", label: "STREAM CONNECTION ISSUE" };
    default:
      return { dot: "bg-accent-500", text: "text-accent-700", label: "L+TV OFFLINE" };
  }
}

export default function DashboardPage() {
  const navigate = useNavigate();
  const live = liveColor(liveStatus.status);
  const today = new Date().toLocaleDateString("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-foreground-950">Dashboard</h1>
          <p className="text-sm text-foreground-600 mt-1">{today} · Welcome back to the L+TV control center.</p>
        </div>
      </div>

      <div
        className="flex flex-col md:flex-row md:items-center gap-4 p-5 rounded-lg border bg-white"
        style={{ borderColor: "oklch(var(--background-200))" }}
      >
        <div className="flex items-center gap-4">
          <span
            className={`w-3 h-3 rounded-full ${live.dot} animate-pulse`}
            aria-hidden="true"
          />
          <div>
            <p className={`text-sm font-bold tracking-wide ${live.text}`}>{live.label}</p>
            <p className="text-xs text-foreground-600 mt-0.5">
              Source: {liveStatus.sourceType} · {liveStatus.currentProgram}
            </p>
          </div>
        </div>
        <div className="md:ml-auto flex items-center gap-3">
          <div className="text-xs text-foreground-600">
            <span className="font-medium text-foreground-800">Up next:</span> {liveStatus.nextProgram}
          </div>
          <button
            type="button"
            onClick={() => navigate("/admin/live")}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-md hover:bg-primary-700 whitespace-nowrap"
          >
            Open Live TV
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-4">
        {dashboardStats.map((stat) => (
          <div key={stat.label} className="bg-white rounded-lg border border-background-200 p-4">
            <div className="flex items-center justify-between">
              <span className={`w-9 h-9 flex items-center justify-center rounded-md ${stat.iconBg}`}>
                <i className={`${stat.icon} ${stat.iconColor} text-base`} />
              </span>
              <span className="text-2xl font-bold text-foreground-950">{stat.value}</span>
            </div>
            <p className="mt-3 text-xs text-foreground-600">{stat.label}</p>
          </div>
        ))}
      </div>

      <div>
        <h2 className="text-sm font-semibold text-foreground-900 mb-3">Quick Actions</h2>
        <div className="flex flex-wrap gap-3">
          {quickActions.map((action) => (
            <button
              key={action.label}
              type="button"
              onClick={() => navigate(action.path)}
              className="flex items-center gap-2 px-4 py-2.5 bg-white border border-background-200 rounded-md text-sm text-foreground-800 hover:border-primary-300 hover:text-primary-700 transition-colors whitespace-nowrap"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className={`${action.icon} text-sm`} />
              </span>
              {action.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <section className="bg-white rounded-lg border border-background-200 overflow-hidden">
          <header className="px-5 py-4 border-b border-background-100 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-foreground-900">Recent Activity</h2>
            <button
              type="button"
              onClick={() => navigate("/admin/activity")}
              className="text-xs text-primary-600 hover:underline whitespace-nowrap"
            >
              View all
            </button>
          </header>
          <ul className="divide-y divide-background-50">
            {recentActivity.map((item) => (
              <li key={item.id} className="flex items-start gap-3 px-5 py-3.5">
                <span className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 shrink-0">
                  <i className={`${activityIcons[item.type] ?? "ri-file-line"} text-sm`} />
                </span>
                <div className="min-w-0">
                  <p className="text-sm text-foreground-800">
                    <span className="font-medium text-foreground-950">{item.user}</span>{" "}
                    {item.action} <span className="font-medium">{item.target}</span>
                  </p>
                  <p className="text-xs text-foreground-500 mt-0.5">{item.time}</p>
                </div>
              </li>
            ))}
          </ul>
        </section>

        <section className="bg-white rounded-lg border border-background-200 overflow-hidden">
          <header className="px-5 py-4 border-b border-background-100">
            <h2 className="text-sm font-semibold text-foreground-900">Pending Tasks</h2>
          </header>
          <ul className="divide-y divide-background-50">
            {pendingTasks.map((task) => (
              <li key={task.id} className="flex items-start gap-3 px-5 py-3.5">
                <span
                  className={`mt-0.5 px-2 py-0.5 rounded text-[10px] font-semibold uppercase whitespace-nowrap ${
                    priorityStyles[task.priority]
                  }`}
                >
                  {task.priority}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-foreground-800">{task.title}</p>
                  <p className="text-xs text-foreground-500 mt-0.5">
                    {task.type} · Due {task.due}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </section>
      </div>
    </div>
  );
}